import { Controller } from "@hotwired/stimulus"
import ApexCharts from "apexcharts"

// Generic report chart (ApexCharts) for server-built chart data, e.g. reporting
// chat answers (ReportingChatsHelper#reporting_chat_chart_data). Value shape:
//   { type: "line"|"bar"|"scatter", stacked: bool, categories: [..],
//     series: [{ name, data: [numbers|null], format }], x_label, y_label, y_format }
// Formats: percent (fractions, 0.12 -> 12%), currency, integer, number.
// Colors: brand purple first, then hues validated for colorblind separation; a
// series keeps its slot by position. Every chart also has a data table next to it.
const PALETTE = ["#734CE0", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#2a78d6"]
const AXIS = "#9CA3AF"

export function formatValue(value, format) {
  if (value === null || value === undefined || Number.isNaN(value)) return ""
  switch (format) {
    case "percent":
      return `${(value * 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`
    case "currency":
      return value.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: Math.abs(value) >= 1000 ? 0 : 2 })
    case "integer":
      return Math.round(value).toLocaleString()
    default:
      return value.toLocaleString(undefined, { maximumFractionDigits: 2 })
  }
}

// ApexCharts writes legend text and tooltip titles/labels with innerHTML. Category
// names come from query results and series names from model-written labels, so
// escape them for those sinks. Axis labels are SVG text and stay raw.
const HTML_ESCAPES = { "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;", "'": "&#39;" }
export function escapeHtml(value) {
  return String(value ?? "").replace(/[&<>"']/g, (character) => HTML_ESCAPES[character])
}

export default class extends Controller {
  // An explicit pixel height: with a percentage, ApexCharts can draw its axis labels
  // and titles past the container, over whatever follows (the caption).
  static values = { chart: Object, height: { type: Number, default: 360 } }

  connect() {
    const options = this.options()
    if (!options) return

    this.chart = new ApexCharts(this.element, options)
    this.chart.render()
  }

  disconnect() {
    if (this.chart) this.chart.destroy()
  }

  // The ApexCharts options for the chart value, or null when there's nothing to draw.
  options() {
    const chart = this.chartValue || {}
    const series = (chart.series || []).slice(0, PALETTE.length)
    const numericX = chart.numeric_x === true
    if (series.length === 0 || (!numericX && (chart.categories || []).length === 0)) return null
    // Rates and counts read honestly from zero; only let the axis float when values go negative.
    const yOf = (point) => (Array.isArray(point) ? point[1] : point)
    const nonNegative = series.every((item) => (item.data || []).every((point) => yOf(point) === null || yOf(point) >= 0))

    return {
      chart: {
        type: chart.type || "bar",
        height: this.heightValue || 360,
        stacked: chart.stacked === true,
        toolbar: { show: false },
        zoom: { enabled: false },
        animations: { enabled: false },
        fontFamily: "'Neue Haas Grotesk Text Pro','DM Sans',sans-serif"
      },
      series: series.map((item) => ({ name: item.name, data: item.data })),
      colors: PALETTE,
      stroke: { width: chart.type === "line" ? 2 : 0, curve: "straight" },
      markers: { size: chart.type === "scatter" ? 6 : (chart.type === "line" ? 4 : 0) },
      plotOptions: { bar: { borderRadius: 4, borderRadiusApplication: "end", columnWidth: "60%" } },
      xaxis: {
        type: numericX ? "numeric" : "category",
        categories: numericX ? undefined : chart.categories,
        title: { text: chart.x_label || undefined, style: { color: AXIS, fontWeight: 500 } },
        labels: {
          style: { colors: AXIS, fontSize: "11px" },
          hideOverlappingLabels: true,
          // Category labels turn 45° when they don't fit side by side; long ones are
          // trimmed on the axis and shown in full in the tooltip.
          rotate: -45,
          trim: true,
          maxHeight: 90,
          formatter: numericX ? (value) => formatValue(value, chart.x_format) : undefined
        },
        axisBorder: { color: "#E5E7EB" },
        tickPlacement: "on"
      },
      yaxis: {
        min: nonNegative ? 0 : undefined,
        title: { text: chart.y_label || undefined, style: { color: AXIS, fontWeight: 500 } },
        labels: { style: { colors: AXIS, fontSize: "11px" }, formatter: (value) => formatValue(value, chart.y_format) }
      },
      // Right padding keeps the last category label from being clipped.
      grid: { borderColor: "#F0F1F3", strokeDashArray: 3, padding: { right: 24 } },
      dataLabels: { enabled: false },
      legend: { show: series.length > 1, position: "top", horizontalAlign: "left", formatter: (name) => escapeHtml(name) },
      tooltip: {
        // A category axis hands the formatter the point's position, so look the name up.
        x: { formatter: numericX ? (value) => formatValue(value, chart.x_format) : (value, { dataPointIndex } = {}) => escapeHtml(chart.categories?.[dataPointIndex] ?? value) },
        shared: chart.type !== "scatter",
        intersect: chart.type === "scatter",
        y: {
          formatter: (value, { seriesIndex }) => formatValue(value, series[seriesIndex]?.format || chart.y_format),
          title: { formatter: (name) => (name ? `${escapeHtml(name)}: ` : "") }
        }
      }
    }
  }
}
